import { motion } from 'framer-motion'
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react'
import toast from 'react-hot-toast'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastProps {
  id: string
  type?: ToastType
  title?: string
  message: string
  duration?: number
}

export const CustomToast = ({ id, type = 'info', title, message }: ToastProps) => {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-400" />,
    error: <XCircle className="w-5 h-5 text-red-400" />,
    warning: <AlertCircle className="w-5 h-5 text-amber-400" />,
    info: <Info className="w-5 h-5 text-blue-400" />,
  }
  
  const borderColors = {
    success: 'border-green-500/30',
    error: 'border-red-500/30',
    warning: 'border-amber-500/30',
    info: 'border-blue-500/30',
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      className={`
        flex items-start gap-3 p-4 rounded-lg
        bg-dark-bg-card border ${borderColors[type]} shadow-xl
        min-w-[320px] max-w-[420px]
      `}
    >
      {icons[type]}
      <div className="flex-1 min-w-0">
        {title && <h4 className="font-medium text-dark-text-primary">{title}</h4>}
        <p className="text-sm text-dark-text-secondary">{message}</p>
      </div>
      <button
        onClick={() => toast.dismiss(id)}
        className="text-dark-text-muted hover:text-dark-text-primary transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  )
}

// Helper functions for toast notifications
export const showToast = {
  success: (message: string, title?: string) => {
    toast.custom((t) => (
      <CustomToast id={t.id} type="success" title={title} message={message} />
    ))
  },
  error: (message: string, title?: string) => {
    toast.custom((t) => (
      <CustomToast id={t.id} type="error" title={title || 'Error'} message={message} />
    ))
  },
  warning: (message: string, title?: string) => {
    toast.custom((t) => (
      <CustomToast id={t.id} type="warning" title={title} message={message} />
    ))
  },
  info: (message: string, title?: string) => {
    toast.custom((t) => (
      <CustomToast id={t.id} type="info" title={title} message={message} />
    ))
  },
}

// Loading toast
export const showLoadingToast = (message: string) => {
  return toast.loading(message, {
    style: {
      background: '#1c2128',
      color: '#f0f6fc',
      border: '1px solid #30363d',
    },
  })
}

export const dismissLoadingToast = (toastId: string, success: boolean, message: string) => {
  toast.dismiss(toastId)
  if (success) {
    showToast.success(message)
  } else {
    showToast.error(message)
  }
}
